// src/hooks/usePaginatedFetch.ts !!DOCUMENTADA!!
import { useState, useCallback, useEffect, useRef } from 'react';
import { logger } from '@/utils/logger';

/**
 * Interface para opções de configuração do hook usePaginatedFetch.
 * @template T Tipo dos itens a serem paginados.
 * @property {function} fetchFunction Função assíncrona que busca dados de uma página, recebendo page e currentItems, retornando { items: T[], totalItems: number }.
 * @property {number} pageSize Número de itens por página (padrão: 10).
 * @property {keyof T | undefined} uniqueKey Chave única para filtrar duplicatas (opcional; se undefined, acumula todos os itens).
 * @property {number} [initialPage] Página inicial (padrão: 0).
 */
interface UsePaginatedFetchOptions<T> {
  fetchFunction: (page: number, currentItems: T[]) => Promise<{ items: T[], totalItems: number }>;
  pageSize: number;
  uniqueKey?: keyof T; // Opcional, undefined por padrão se não fornecido
  initialPage?: number;
  enabled?: boolean;
}

/**
 * Interface para o resultado retornado pelo hook usePaginatedFetch.
 * @template T Tipo dos itens a serem paginados.
 * @property {T[]} items Lista de itens carregados.
 * @property {boolean} hasMore Indica se há mais itens para carregar.
 * @property {boolean} isLoading Flag indicando se está em carregamento.
 * @property {function} loadMore Função para carregar a próxima página.
 * @property {function} reset Função para reiniciar o estado e carregar a página inicial.
 * @property {number} totalItems Total de itens disponíveis na API.
 * @property {function} setTotalItems Função para atualizar o total de itens manualmente.
 * @property {function} updateItems Função para atualizar a lista de itens manualmente.
 */
interface UsePaginatedFetchResult<T> {
  items: T[];
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  reset: () => void;
  totalItems: number;
  setTotalItems: (total: number) => void;
  updateItems: (updater: (prevItems: T[]) => T[]) => void;
}

/**
 * Hook personalizado para gerenciar paginação infinita de dados.
 * @template T Tipo dos itens a serem paginados.
 * @param {UsePaginatedFetchOptions<T>} options Opções de configuração do hook.
 * @returns {UsePaginatedFetchResult<T>} Objeto com estado e métodos para controle da paginação.
 * 
 * @description
 * Este hook facilita a implementação de paginação infinita ao gerenciar o carregamento de páginas
 * adicionais de dados de uma API. Ele suporta tanto cenários com chaves únicas (para evitar duplicatas)
 * quanto cenários sem chaves únicas (para acumular todos os itens, como em tabelas com repetições).
 * 
 * - **Funcionamento**: 
 *   - Inicializa com a página `initialPage` e carrega os dados usando `fetchFunction`.
 *   - Acumula itens em `items`, aplicando filtro de duplicatas se `uniqueKey` for fornecido.
 *   - Atualiza `hasMore` com base em `totalFetched` e `newItems.length`, parando quando os dados se esgotam.
 * - **Casos de Uso**:
 *   - Com `uniqueKey` (ex.: `codUsuario` em `SearchSupplierModalContent.tsx`): Filtra duplicatas.
 *   - Sem `uniqueKey` (ex.: `undefined` em `TableIntegrationSidePanelContent.tsx`): Acumula todos os itens, ideal para dados repetidos.
 * - **Métodos**:
 *   - `loadMore`: Carrega a próxima página se `hasMore` for verdadeiro.
 *   - `reset`: Reinicia o estado e carrega a página inicial.
 *   - `updateItems`: Permite atualizações manuais da lista de itens.
 * 
 * @example
 * // Uso com chave única
 * const { items, hasMore, loadMore } = usePaginatedFetch({
 *   fetchFunction: async (page) => ({ items: [], totalItems: 10 }),
 *   pageSize: 10,
 *   uniqueKey: 'id',
 *   initialPage: 0,
 * });
 * 
 * // Uso sem chave única
 * const { items, hasMore, loadMore } = usePaginatedFetch({
 *   fetchFunction: async (page) => ({ items: [], totalItems: 10 }),
 *   pageSize: 10,
 *   uniqueKey: undefined,
 *   initialPage: 0,
 * });
 */
export function usePaginatedFetch<T>({
  fetchFunction,
  pageSize: _pageSize,
  uniqueKey,
  initialPage = 0,
  enabled = true,
}: UsePaginatedFetchOptions<T>): UsePaginatedFetchResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const isFetchingRef = useRef(false);
  const itemsRef = useRef<T[]>([]);

  // Sync itemsRef com items — permite ler items dentro de loadData sem incluir como dep
  // (evita loop infinito: items dep → loadData muda → reset muda → useEffect dispara → items muda).
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const loadData = useCallback(async (pageNumber: number, resetItems = false) => {
    if (isFetchingRef.current) {
      logger.log(`loadData não chamado - página ${pageNumber}, isFetching: true`);
      return;
    }
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const baseItems = resetItems ? [] : itemsRef.current;
      const { items: newItems, totalItems: fetchedTotalItems } = await fetchFunction(pageNumber, baseItems);
      const totalFetched = resetItems ? newItems.length : baseItems.length + newItems.length;

      setItems((prev) => {
        if (uniqueKey === undefined) {
          // Acumula todos os itens sem filtrar duplicatas, adequado para dados repetidos
          logger.log(`Itens adicionados (página ${pageNumber}, sem uniqueKey):`, newItems);
          return resetItems ? newItems : [...prev, ...newItems];
        }
        // Filtra duplicatas com base na chave única fornecida
        const existingIds = new Set(prev.map(item => item[uniqueKey]));
        const uniqueNewItems = newItems.filter(item => !existingIds.has(item[uniqueKey]));
        logger.log(`Itens adicionados (página ${pageNumber}):`, uniqueNewItems);
        return resetItems ? uniqueNewItems : [...prev, ...uniqueNewItems];
      });

      if (resetItems) {
        setTotalItems(fetchedTotalItems);
      }

      // Define hasMore como falso quando não há mais itens ou totalItems é atingido
      setHasMore(totalFetched < fetchedTotalItems && newItems.length > 0);
      setPage(pageNumber);
    } catch (err) {
      logger.error('Erro ao carregar dados:', err);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchFunction, uniqueKey]);

  const loadMore = useCallback(() => {
    if (isLoading || !hasMore) {
      logger.log('loadMore não chamado - isLoading:', isLoading, 'hasMore:', hasMore);
      return;
    }
    logger.log('loadMore chamado');
    loadData(page + 1);
  }, [isLoading, hasMore, page, loadData]);

  // reset depende de loadData; quando fetchFunction muda (via useCallback deps no consumidor),
  // loadData muda → reset muda → useEffect interno [reset, enabled] dispara refetch.
  // Isso elimina a necessidade de useEffect externo de refetch-on-search-change nos consumidores.
  const reset = useCallback(() => {
    setItems([]);
    setPage(initialPage);
    setHasMore(true);
    setTotalItems(0);
    loadData(initialPage, true);
  }, [initialPage, loadData]);

  const updateItems = useCallback((updater: (prevItems: T[]) => T[]) => {
    setItems(prev => updater(prev));
  }, []);

  useEffect(() => {
    if (enabled) reset();
  }, [reset, enabled]);

  return {
    items,
    hasMore,
    isLoading,
    loadMore,
    reset,
    totalItems,
    setTotalItems,
    updateItems,
  };
}